import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );

    // Listen to slideshow select events to save state on manual navigation
    this.slideshow?.addEventListener(
      "slideshow:select",
      this.#handleSlideshowSelect,
      { signal }
    );

    // Listen to slideshow select events to update dot aria-selected states after filtering
    this.slideshow?.addEventListener(
      "slideshow:select",
      this.#handleSlideshowSelectForDots,
      { signal, capture: true }
    );
    
    // Also update dot state periodically to ensure it stays in sync
    if (this.slideshow && typeof setInterval !== "undefined") {
      const intervalId = setInterval(() => {
        if (this.slideshow) {
          this.#updateDotSelectionState();
        } else {
          if (typeof clearInterval !== "undefined") {
            clearInterval(intervalId);
          }
        }
      }, 500);
      
      signal?.addEventListener("abort", () => {
        if (typeof clearInterval !== "undefined") {
          clearInterval(intervalId);
        }
      });
    }

    // Listen to slideshow select events for quick-add-modal to ensure slide stays visible
    this.slideshow?.addEventListener(
      "slideshow:select",
      this.#handleSlideshowSelectForQuickAdd,
      { signal }
    );

    // Set up MutationObserver for quick-add-modal to keep slide visible
    this.#setupQuickAddModalObserver(signal);

    // Add click handler for videos in zoomable containers
    // This ensures videos can open the zoom dialog like images
    if (this.refs.zoomDialogComponent) {
      this.addEventListener("click", this.#handleVideoZoomClick, {
        signal,
        capture: true,
      });
    }

    // Add click handler to intercept zoom clicks and correct index for filtered media
    if (this.refs.zoomDialogComponent) {
      this.addEventListener("click", this.#handleZoomClick, {
        signal,
        capture: true,
      });
    }

    // Initialize with selected variant on page load (for refresh)
    // This will call #ensureDotsVisible() after filtering completes
    this.#initializeWithSelectedVariant();

    // CRITICAL: Direct call to ensureDotsVisible with multiple delays
    // This ensures dots are visible even if filtering hasn't completed yet
    const ensureDots = () => {
      if (this.slideshow) {
        this.#ensureDotsVisible();
      }
    };

    // Call immediately and with multiple delays
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ensureDots();
        setTimeout(ensureDots, 100);
        setTimeout(ensureDots, 300);
        setTimeout(ensureDots, 500);
        setTimeout(ensureDots, 800);
        setTimeout(ensureDots, 1000);
      });
    });

    // Handle viewport changes (web to mweb or vice versa)
    this.#handleViewportChange(signal);
  }

  /**
   * Injects CSS rule to hide dots with data-filtered="hidden"
   */
  #injectDotFilterCSS() {
    if (typeof document === "undefined" || !document.head) {
      return;
    }

    const styleId = "media-gallery-dot-filter";
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement("style");
      if (styleEl) {
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
    }

    // CSS rule to completely remove dots from layout flow when hidden
    // This ensures they don't affect flex centering
    styleEl.textContent = `
      .slideshow-controls__dots li[data-filtered="hidden"] {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        position: absolute !important;
        left: -9999px !important;
      }
      .slideshow-controls__dots button[data-filtered="hidden"] {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      /* Ensure visible dots are shown */
      .slideshow-controls__dots li[data-filtered="visible"] {
        display: list-item !important;
        position: static !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      .slideshow-controls__dots button[data-filtered="visible"] {
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      /* Ensure dots container stays centered after filtering and has high z-index */
      .slideshow-controls__dots {
        justify-content: center !important;
        display: flex !important;
        align-items: center !important;
        position: relative !important;
        z-index: 1000 !important;
      }
      /* Ensure dots buttons are above everything and have pointer cursor */
      .slideshow-controls__dots button {
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      .slideshow-controls__dots li {
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      /* Ensure mobile controls container is above slides */
      .media-gallery__mobile-controls {
        position: relative !important;
        z-index: 1000 !important;
      }
      /* Prevent video containers from capturing clicks meant for dots */
      slideshow-slide.product-media-container--video,
      slideshow-slide.product-media-container--external_video,
      .product-media-container--zoomable.product-media-container--video,
      .product-media-container--zoomable.product-media-container--external_video {
        z-index: 1 !important;
      }
    `;
  }

  /**
   * Handles viewport changes (web to mweb or vice versa)
   * Re-applies variant filtering when switching between desktop and mobile views
   * @param {AbortSignal} signal - The abort signal for cleanup
   */
  #handleViewportChange(signal) {
    if (typeof window === "undefined" || !window?.matchMedia) {
      return;
    }

    if (!signal) {
      return;
    }

    const breakpoint = 750; // Match the breakpoint used in the code
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);

    if (!mediaQuery) {
      return;
    }

    // Track previous state
    let wasDesktop = mediaQuery.matches || false;

    /** @type {ReturnType<typeof setTimeout> | null} */
    let resizeTimeout = null;

    const handleViewportChange = () => {
      if (!mediaQuery) {
        return;
      }

      // Clear previous timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }

      // Debounce to avoid too many calls
      resizeTimeout = setTimeout(() => {
        if (!mediaQuery) {
          return;
        }

        const isDesktop = mediaQuery.matches || false;

        // Only re-apply if viewport crossed the breakpoint
        if (wasDesktop !== isDesktop) {
          wasDesktop = isDesktop;

          // Wait for layout to settle
          if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(() => {
              if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(() => {
                  // Re-apply variant filtering based on current viewport
                  if (
                    this &&
                    typeof this.#reapplyVariantFiltering === "function"
                  ) {
                    this.#reapplyVariantFiltering();
                  }
                });
              }
            });
          }
        }
      }, 200);
    };

    // Use matchMedia listener (more reliable for breakpoint detection)
    if (
      mediaQuery.addEventListener &&
      typeof mediaQuery.addEventListener === "function"
    ) {
      // Modern browsers
      try {
        mediaQuery.addEventListener("change", handleViewportChange, { signal });
      } catch (e) {
        // Fallback if addEventListener fails
        console.warn("[MediaGallery] Failed to add matchMedia listener", e);
      }
    } else if (
      mediaQuery.addListener &&
      typeof mediaQuery.addListener === "function"
    ) {
      // Legacy browsers
      try {
        mediaQuery.addListener(handleViewportChange);
        // Cleanup on disconnect
        if (signal && typeof signal.addEventListener === "function") {
          signal.addEventListener("abort", () => {
            if (
              mediaQuery.removeListener &&
              typeof mediaQuery.removeListener === "function"
            ) {
              mediaQuery.removeListener(handleViewportChange);
            }
          });
        }
      } catch (e) {
        console.warn(
          "[MediaGallery] Failed to add matchMedia listener (legacy)",
          e
        );
      }
    }

    // Also listen to resize as fallback
    if (
      window.addEventListener &&
      typeof window.addEventListener === "function"
    ) {
      try {
        window.addEventListener("resize", handleViewportChange, { signal });
      } catch (e) {
        console.warn("[MediaGallery] Failed to add resize listener", e);
      }
    }

    // Listen to orientation change (for mobile devices)
    if (typeof window.orientation !== "undefined" && window.addEventListener) {
      try {
        window.addEventListener("orientationchange", handleViewportChange, {
          signal,
        });
      } catch (e) {
        console.warn(
          "[MediaGallery] Failed to add orientationchange listener",
          e
        );
      }
    }
  }

  /**
   * Re-applies variant filtering when viewport changes
   * Checks current variant and re-filters media accordingly
   */
  #reapplyVariantFiltering() {
    if (!this) {
      return;
    }

    // Use the existing initVariantMedia method which handles both URL and picker cases
    // Wait a bit for slideshow to be ready if switching to mobile
    const isMobile =
      typeof window !== "undefined" &&
      window?.matchMedia &&
      typeof window.matchMedia === "function";

    let shouldWaitForSlideshow = false;

    if (isMobile) {
      try {
        const mediaQuery = window.matchMedia("(min-width: 750px)");
        if (mediaQuery) {
          shouldWaitForSlideshow = !mediaQuery.matches && !!this.slideshow;
        }
      } catch (e) {
        // If matchMedia fails, assume desktop
        shouldWaitForSlideshow = false;
      }
    }

    if (shouldWaitForSlideshow) {
      // For mobile, wait for slideshow to be ready
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => {
          if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(() => {
              if (this && typeof this.#initVariantMedia === "function") {
                this.#initVariantMedia();
              }
            });
          }
        });
      }
    } else {
      // For desktop or if slideshow not needed, apply immediately
      if (this && typeof this.#initVariantMedia === "function") {
        this.#initVariantMedia();
      }
    }
  }

  /**
   * Ensures dots are visible when there are multiple slides.
   * Called on initial load and after filtering.
   */
  #ensureDotsVisible() {
    if (!this.slideshow) {
      return;
    }

    // Don't show dots in quick-add-modal
    const isInQuickAddModal =
      this.closest("dialog")?.classList?.contains("quick-add-modal") || false;
    if (isInQuickAddModal) {
      // Hide all controls and dots in quick-add-modal
      const slideshowControls = this.slideshow?.refs?.slideshowControls;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
        slideshowControls.style.setProperty("display", "none", "important");
      }
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Get mobile dots container (for mobile web)
        const mobileControls = this.querySelector(
          ".media-gallery__mobile-controls"
        );
        const mobileDotsContainer = mobileControls?.querySelector(
          "ol.slideshow-controls__dots"
        );

        // Get dots - prioritize mobile dots, fallback to slideshow refs
        let dots = /** @type {HTMLElement[]} */ ([]);
        if (mobileDotsContainer) {
          const allDots = mobileDotsContainer.querySelectorAll("li > button");
          dots = Array.from(allDots).filter((d) => d instanceof HTMLElement);
        }

        // Fallback to slideshow refs if mobile dots not found
        if (dots.length === 0 && this.slideshow?.refs?.dots) {
          dots = Array.isArray(this.slideshow.refs.dots)
            ? this.slideshow.refs.dots.filter((d) => d instanceof HTMLElement)
            : [];
        }

        const filteredSlides = this.slideshow?.slides;
        const slideshowControls = this.slideshow?.refs?.slideshowControls;
        if (!filteredSlides || !dots || dots.length === 0) return;

        // CRITICAL: Only hide dots that are explicitly marked as "hidden"
        // Don't hide dots that don't have the attribute yet (filtering might not be complete)
        dots.forEach((dot) => {
          if (!dot) return;
          const filteredState = dot.getAttribute("data-filtered");
          // Only hide if explicitly marked as "hidden", not if attribute is missing
          if (filteredState === "hidden") {
            dot.hidden = true;
            dot.style.setProperty("display", "none", "important");
            const dotLi = dot.closest("li");
            if (dotLi instanceof HTMLElement) {
              dotLi.hidden = true;
              dotLi.style.setProperty("display", "none", "important");
            }
          }
        });

        // Count visible dots (those with data-filtered="visible" OR no attribute yet)
        const visibleDots = dots.filter((dot) => {
          if (!dot) return false;
          const filteredState = dot.getAttribute("data-filtered");
          // Consider visible if: explicitly "visible" OR no attribute set yet (filtering pending)
          return filteredState === "visible" || filteredState === null;
        });

        // If there are multiple visible slides AND visible dots, ensure controls and dots are visible
        if (filteredSlides.length > 1 && visibleDots.length > 1) {
          // Force controls to be visible - use !important to override any CSS
          if (slideshowControls instanceof HTMLElement) {
            slideshowControls.hidden = false;
            slideshowControls.style.setProperty(
              "display",
              "block",
              "important"
            );
          }

          // Only show dots that are marked as visible and set up click handlers
          visibleDots.forEach((dot, dotIndex) => {
            if (dot) {
              dot.style.setProperty("display", "flex", "important");
              dot.hidden = false;
              dot.style.setProperty("pointer-events", "auto", "important");
              dot.style.setProperty("cursor", "pointer", "important");
              const dotLi = dot.closest("li");
              if (dotLi instanceof HTMLElement) {
                dotLi.style.setProperty("display", "list-item", "important");
                dotLi.hidden = false;
                dotLi.style.setProperty("pointer-events", "auto", "important");
                dotLi.style.setProperty("cursor", "pointer", "important");
              }

              // Always set up click handler - use visible dot index directly
              const slideId = dot.getAttribute("data-slide-id");
              const visibleDotIndex = visibleDots.indexOf(dot);

              // Remove any existing handlers
              dot.removeAttribute("on:click");
              dot.onclick = null;

              // Set up new click handler
              dot.onclick = (e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                }
                if (
                  this.slideshow &&
                  typeof this.slideshow.select === "function"
                ) {
                  if (slideId) {
                    // Try slideId first
                    this.slideshow.select({ id: slideId });
                  } else if (
                    visibleDotIndex >= 0 &&
                    visibleDotIndex < filteredSlides.length
                  ) {
                    // Use visible dot index - this should match the filtered slide index
                    this.slideshow.select(visibleDotIndex);
                  }
                }
                return false;
              };
            }
          });

          // Ensure dots container is visible
          const dotsContainer = dots[0]?.closest("ol");
          if (dotsContainer instanceof HTMLElement) {
            dotsContainer.style.setProperty("display", "flex", "important");
            dotsContainer.hidden = false;
          }

          // Update aria-selected state based on currently selected slide
          // Use setTimeout to ensure this runs after DOM updates
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 50);
        } else {
          // Hide controls if there's only one slide or one visible dot
          if (slideshowControls instanceof HTMLElement) {
            slideshowControls.hidden = true;
            slideshowControls.style.setProperty("display", "none", "important");
          }
        }
      });
    });
  }
  
  /**
   * Updates the aria-selected state of dots based on currently selected slide
   */
  #updateDotSelectionState() {
    if (!this.slideshow) return;
    
    // Get current slide index
    const currentIndex = typeof this.slideshow.current === "number" 
      ? this.slideshow.current 
      : 0;
    
    // Create a synthetic event to trigger the dot selection handler
    const syntheticEvent = new CustomEvent("slideshow:select", {
      detail: { index: currentIndex }
    });
    
    // Call the handler to update dot states
    this.#handleSlideshowSelectForDots(syntheticEvent);
  }

  /**
   * Handles clicks on videos in zoomable containers to open zoom dialog
   * @param {Event} event - The click event
   */
  #handleVideoZoomClick = (event) => {
    // Only handle if zoom dialog exists
    if (!this.refs.zoomDialogComponent) {
      return;
    }

    // Check if click is on a video in a zoomable container
    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }

    // Find the closest video container - video might be nested in deferred-media > product-media > product-media-container
    let videoContainer = target.closest(
      ".product-media-container--zoomable.product-media-container--video, .product-media-container--zoomable.product-media-container--external_video"
    );

    // If not found, try finding through deferred-media or product-media
    if (!videoContainer) {
      const deferredMedia = target.closest("deferred-media");
      if (deferredMedia) {
        videoContainer = deferredMedia.closest(
          ".product-media-container--zoomable.product-media-container--video, .product-media-container--zoomable.product-media-container--external_video"
        );
      }
    }

    // If still not found, try finding through product-media
    if (!videoContainer) {
      const productMedia = target.closest(".product-media");
      if (productMedia) {
        videoContainer = productMedia.closest(
          ".product-media-container--zoomable.product-media-container--video, .product-media-container--zoomable.product-media-container--external_video"
        );
      }
    }

    // If still not found, try just finding any product-media-container with video class
    if (!videoContainer) {
      videoContainer = target.closest(
        ".product-media-container--video, .product-media-container--external_video"
      );
      if (videoContainer) {
        // Check if zoom is enabled by looking for zoom-dialog in the media-gallery
        const hasZoomDialog = this.refs.zoomDialogComponent !== null;

        // If zoom dialog exists, treat it as zoomable even if class is missing
        if (
          !hasZoomDialog &&
          !videoContainer.classList.contains(
            "product-media-container--zoomable"
          )
        ) {
          videoContainer = null;
        }
      }
    }

    if (!videoContainer) {
      return;
    }

    // Don't handle if clicking on buttons or controls
    const buttonOrControl = target.closest("button, video[controls], iframe");
    if (buttonOrControl) {
      // Check if it's actually the controls - if so, allow it
      const isVideoControls =
        target.closest("video[controls]") &&
        (target.tagName === "VIDEO" ||
          target.closest("video[controls]") === target);
      if (!isVideoControls) {
        return;
      }
    }

    // Find the slideshow-slide that contains this video (for slideshow layout)
    // OR find the index in grid layout (for grid layout)
    let slide = videoContainer.closest("slideshow-slide");
    let slideIndex = -1;

    if (slide) {
      // Check if the slide has a zoom handler
      const zoomHandler = slide.getAttribute("on:click");
      if (!zoomHandler?.includes("zoom-dialog")) {
        return;
      }

      // Extract the index from the zoom handler or find it from the slide
      const slides = this.slideshow?.slides;
      if (!slides) {
        return;
      }

      const slidesArray = Array.isArray(slides) ? slides : Array.from(slides);
      slideIndex = slidesArray.indexOf(slide);
      if (slideIndex === -1) {
        return;
      }
    } else {
      // Grid layout - find index by looking at all media containers
      const isGrid = this.classList.contains("media-gallery--grid");

      if (isGrid) {
        // For grid layout, we need to find the index in the zoom dialog's media array
        // Get the media ID from the product-media element
        const productMedia = videoContainer.querySelector(".product-media");
        const mediaId = productMedia?.getAttribute("data-media-id");

        // Find the index in the zoom dialog's media array by matching media ID
        const zoomDialog = this.refs.zoomDialogComponent;
        if (zoomDialog && zoomDialog.refs?.media) {
          const mediaArray = Array.from(zoomDialog.refs.media);

          // Find the index by matching the media ID
          slideIndex = mediaArray.findIndex((mediaItem) => {
            const mediaElement = mediaItem.querySelector(".product-media");
            const itemMediaId = mediaElement?.getAttribute("data-media-id");
            return itemMediaId === mediaId;
          });

          if (slideIndex === -1) {
            // Fallback: try to find by position in grid
            const allContainers = this.querySelectorAll(
              ".product-media-container"
            );
            const containersArray = Array.from(allContainers);
            const gridIndex = containersArray.indexOf(videoContainer);

            // If grid index is within zoom dialog media array bounds, use it
            if (gridIndex >= 0 && gridIndex < mediaArray.length) {
              slideIndex = gridIndex;
            } else {
              return;
            }
          }
        } else {
          return;
        }
      } else {
        return;
      }
    }

    // Open the zoom dialog
    event.preventDefault();
    event.stopPropagation();
    // Cast event to PointerEvent for zoom method
    this.zoom(slideIndex, /** @type {PointerEvent} */ (event));

    // Ensure the correct media is selected after opening
    // The zoom dialog's open method should handle this, but we'll ensure it's correct
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.refs.zoomDialogComponent) {
          const zoomDialog = this.refs.zoomDialogComponent;
          const media = zoomDialog.refs?.media;
          const targetMedia = media?.[slideIndex];

          if (targetMedia) {
            // Scroll the dialog content to show the target media
            const dialog = zoomDialog.refs?.dialog;
            if (dialog) {
              targetMedia.scrollIntoView({
                behavior: "instant",
                block: "center",
                inline: "center",
              });
            }

            // Also ensure thumbnail is selected
            if (typeof zoomDialog.selectThumbnail === "function") {
              zoomDialog.selectThumbnail(slideIndex, { behavior: "instant" });
            }
          }
        }
      });
    });
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by filtering media to show only matching variant.
   * Matches variant color value with media alt text (images) or aria-label (videos).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (!event?.detail) return;
    const variant = event.detail?.resource;

    if (!variant?.id) {
      const source = event.detail?.data?.html;
      const newMediaGallery = source?.querySelector("media-gallery");
      if (newMediaGallery) {
        this.replaceWith(newMediaGallery);
      }
      return;
    }

    // Get color value from variant (option1, option2, or option3)
    // @ts-ignore - variant has option1/option2/option3 at runtime
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
    let variantColor =
      // @ts-ignore - variant has option1/option2/option3 at runtime
      variant["option1"] || variant["option2"] || variant["option3"];

    // Fallback: Try to get color from selected input value
    // Look in both .shopify-section (PDP) and dialog (quick-add-modal)
    if (!variantColor) {
      const parent = this.closest(".shopify-section, dialog");
      const variantPicker = parent?.querySelector("variant-picker");
      if (variantPicker) {
        const checkedInput = variantPicker.querySelector(
          'input[type="radio"]:checked, select option:checked'
        );
        if (checkedInput instanceof HTMLInputElement) {
          variantColor = checkedInput.value;
        } else if (checkedInput instanceof HTMLOptionElement) {
          variantColor = checkedInput.value || checkedInput.textContent;
        }
      }
    }

    if (!variantColor) {
      const source = event.detail?.data?.html;
      const newMediaGallery = source?.querySelector("media-gallery");
      if (newMediaGallery) {
        this.replaceWith(newMediaGallery);
      }
      return;
    }

    // Normalize color for comparison (lowercase, trim)
    if (!variantColor) return;
    const normalizedColor = String(variantColor).toLowerCase().trim();
    if (!normalizedColor) return;

    // Filter media to show only matching variant (including first variant)
    this.#filterMediaByColor(normalizedColor);

    // Also filter zoom dialog if it's open
    if (this.refs.zoomDialogComponent?.refs?.dialog?.open) {
      this.#filterZoomDialogMedia();
    }
    
    // CRITICAL: Update dot selection state after variant update
    // Use multiple delays to ensure filtering has completed
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 200);
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 400);
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 600);
      });
    });
  };

  /**
   * Filters media to show only items matching the variant color.
   * Hides media that don't match based on alt text (images) or aria-label (videos).
   * @param {string} normalizedColor - The normalized color string to match.
   */
  #filterMediaByColor(normalizedColor) {
    if (!normalizedColor) return;
    // Inject CSS to hide dots with data-filtered="hidden"
    this.#injectDotFilterCSS();

    // Check if we're in quick-add-modal
    const isInQuickAddModal =
      this.closest("dialog")?.classList?.contains("quick-add-modal") || false;

    // Check if desktop grid layout
    const isDesktopGrid =
      typeof window !== "undefined" &&
      window?.matchMedia &&
      window.matchMedia("(min-width: 750px)")?.matches &&
      this.classList?.contains("media-gallery--grid");

    // For slideshow (mobile/carousel)
    if (this.slideshow && !isDesktopGrid) {
      const slides = this.slideshow?.refs?.slides || this.slideshow?.slides;
      if (slides) {
        const slidesArray = Array.isArray(slides)
          ? slides
          : slides
          ? Array.from(slides)
          : null;
        if (!slidesArray || slidesArray.length === 0) return;

        // Ensure slides are 100% width with no gaps for single image view
        const slidesContainer = this.slideshow?.refs?.scroller;
        if (slidesContainer instanceof HTMLElement) {
          slidesContainer.style.setProperty("gap", "0", "important");
          // Remove any gutters
          slidesContainer.removeAttribute("gutters");
        }

        // Ensure each slide is 100% width
        slidesArray.forEach((slide) => {
          if (slide instanceof HTMLElement) {
            slide.style.setProperty("width", "100%", "important");
            slide.style.setProperty("flex-shrink", "0", "important");
          }
        });

        let firstMatchingSlide = null;

        // Get mobile dots - query directly from DOM
        const mobileControls = this.querySelector(
          ".media-gallery__mobile-controls"
        );
        const mobileDotsContainer = mobileControls?.querySelector(
          "ol.slideshow-controls__dots"
        );

        // Get ALL dots from mobile controls container - they should be in order matching slides
        let dots = /** @type {HTMLElement[]} */ ([]);
        if (mobileDotsContainer) {
          // Get all button elements inside the ol - they are in order (li > button)
          const allDots = mobileDotsContainer.querySelectorAll("li > button");
          dots = Array.from(allDots).filter((d) => d instanceof HTMLElement);
        }

        // Fallback to slideshow refs if mobile dots not found
        if (dots.length === 0 && this.slideshow?.refs?.dots) {
          dots = Array.isArray(this.slideshow.refs.dots)
            ? this.slideshow.refs.dots.filter((d) => d instanceof HTMLElement)
            : [];
        }

        // CRITICAL: Verify dots array matches slides array length
        // If they don't match, we can't reliably map dots to slides
        if (dots.length !== slidesArray.length) {
          console.warn(
            `[MediaGallery] Dots count (${dots.length}) doesn't match slides count (${slidesArray.length}). Cannot filter dots correctly.`
          );
          // Still proceed, but be aware of mismatch
        }

        // STEP 1: Hide ALL dots first - completely remove from layout flow
        dots.forEach((dot, index) => {
          if (dot instanceof HTMLElement) {
            // Set data attribute first (CSS will handle the rest)
            dot.setAttribute("data-filtered", "hidden");
            dot.hidden = true;

            // Also set inline styles as backup
            dot.style.setProperty("display", "none", "important");
            dot.style.setProperty("visibility", "hidden", "important");
            dot.style.setProperty("opacity", "0", "important");
            dot.style.setProperty("width", "0", "important");
            dot.style.setProperty("height", "0", "important");
            dot.style.setProperty("margin", "0", "important");
            dot.style.setProperty("padding", "0", "important");

            const dotLi = dot.closest("li");
            if (dotLi instanceof HTMLElement) {
              // CRITICAL: Remove li from layout flow completely
              dotLi.setAttribute("data-filtered", "hidden");
              dotLi.hidden = true;
              dotLi.style.setProperty("display", "none", "important");
              dotLi.style.setProperty("position", "absolute", "important");
              dotLi.style.setProperty("left", "-9999px", "important");
              dotLi.style.setProperty("width", "0", "important");
              dotLi.style.setProperty("height", "0", "important");
              dotLi.style.setProperty("margin", "0", "important");
              dotLi.style.setProperty("padding", "0", "important");
              dotLi.style.setProperty("visibility", "hidden", "important");
              dotLi.style.setProperty("opacity", "0", "important");
            }
          }
        });

        // STEP 3: Filter slides and show/hide corresponding dots
        // dots[i] corresponds to slidesArray[i] - they are in the same order
        for (let i = 0; i < slidesArray.length; i++) {
          const slide = slidesArray[i];
          if (!(slide instanceof HTMLElement)) continue;

          const mediaElement = slide.querySelector("img, video, iframe");
          const slideId = slide?.getAttribute?.("slide-id") || null;
          const dot =
            dots && dots[i] && dots[i] instanceof HTMLElement ? dots[i] : null;

          if (mediaElement && mediaElement instanceof HTMLElement) {
            const matches = this.#mediaMatchesColor(
              mediaElement,
              normalizedColor
            );

            if (matches) {
              // For quick-add-modal: show only the first matching slide, hide all others
              if (isInQuickAddModal) {
                if (!firstMatchingSlide) {
                  // This is the first matching slide - show it
                  firstMatchingSlide = slide;
                  slide.removeAttribute("hidden");
                  // Remove any inline styles that might hide it
                  slide.style.removeProperty("display");
                  slide.style.removeProperty("visibility");
                  slide.style.removeProperty("opacity");
                } else {
                  // This is not the first matching slide - hide it
                  slide.setAttribute("hidden", "");
                }
              } else {
                // For regular PDP: show all matching slides
                slide.removeAttribute("hidden");
                slide.style.removeProperty("display");
              }

              // Show the corresponding dot at index i (only if not in quick-add-modal)
              if (dot && !isInQuickAddModal) {
                dot.setAttribute("data-filtered", "visible");
                dot.hidden = false;
                // Explicitly set display to flex to show the dot
                dot.style.setProperty("display", "flex", "important");
                dot.style.setProperty("visibility", "visible", "important");
                dot.style.setProperty("opacity", "1", "important");
                // Remove width/height constraints
                dot.style.removeProperty("width");
                dot.style.removeProperty("height");
                dot.style.removeProperty("margin");
                dot.style.removeProperty("padding");

                const dotLi = dot.closest("li");
                if (dotLi instanceof HTMLElement) {
                  // CRITICAL: Restore li to normal layout flow
                  dotLi.setAttribute("data-filtered", "visible");
                  dotLi.hidden = false;
                  dotLi.style.setProperty("display", "list-item", "important");
                  dotLi.style.setProperty("visibility", "visible", "important");
                  dotLi.style.setProperty("opacity", "1", "important");
                  // Remove position absolute and other constraints
                  dotLi.style.removeProperty("position");
                  dotLi.style.removeProperty("left");
                  dotLi.style.removeProperty("width");
                  dotLi.style.removeProperty("height");
                  dotLi.style.removeProperty("margin");
                  dotLi.style.removeProperty("padding");
                }
                // Update click handler - use slideId if available, otherwise use index
                if (dot) {
                  if (slideId) {
                    dot.setAttribute("data-slide-id", slideId);
                  } else {
                    dot.removeAttribute("data-slide-id");
                  }
                  dot.removeAttribute("on:click");
                  dot.onclick = (/** @type {Event} */ e) => {
                    if (e) {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                    }
                    if (
                      this.slideshow &&
                      typeof this.slideshow.select === "function"
                    ) {
                      if (slideId) {
                        // Use slideId if available
                        this.slideshow.select({ id: slideId });
                      } else {
                        // Fallback to index
                        this.slideshow.select(i);
                      }
                    }
                    return false;
                  };
                  // Ensure pointer-events are enabled
                  dot.style.removeProperty("pointer-events");
                }
              }

              if (!firstMatchingSlide) {
                firstMatchingSlide = slide;
              }
            } else {
              // Hide the slide
              slide.setAttribute("hidden", "");
              slide.style.display = "";

              // Hide the corresponding dot at index i - remove from layout flow
              if (dot) {
                dot.setAttribute("data-filtered", "hidden");
                dot.hidden = true;
                dot.style.setProperty("display", "none", "important");

                const dotLi = dot.closest("li");
                if (dotLi instanceof HTMLElement) {
                  // CRITICAL: Remove li from layout flow completely using position absolute
                  dotLi.setAttribute("data-filtered", "hidden");
                  dotLi.hidden = true;
                  dotLi.style.setProperty("display", "none", "important");
                  dotLi.style.setProperty("position", "absolute", "important");
                  dotLi.style.setProperty("left", "-9999px", "important");
                  dotLi.style.setProperty("width", "0", "important");
                  dotLi.style.setProperty("height", "0", "important");
                  dotLi.style.setProperty("margin", "0", "important");
                  dotLi.style.setProperty("padding", "0", "important");
                }
              }
            }
          } else {
            // No media element - show by default
            slide.removeAttribute("hidden");
            slide.style.display = "";

            if (dot) {
              dot.style.setProperty("display", "flex", "important");
              dot.hidden = false;
              const dotLi = dot.closest("li");
              if (dotLi instanceof HTMLElement) {
                dotLi.style.setProperty("display", "list-item", "important");
                dotLi.hidden = false;
              }
              // Set up click handler - use slideId if available, otherwise use index
              if (dot) {
                if (slideId) {
                  dot.setAttribute("data-slide-id", slideId);
                } else {
                  dot.removeAttribute("data-slide-id");
                }
                dot.removeAttribute("on:click");
                dot.onclick = (e) => {
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                  }
                  if (
                    this.slideshow &&
                    typeof this.slideshow.select === "function"
                  ) {
                    if (slideId) {
                      // Use slideId if available
                      this.slideshow.select({ id: slideId });
                    } else {
                      // Fallback to index
                      this.slideshow.select(i);
                    }
                  }
                  return false;
                };
                // Ensure pointer-events are enabled
                dot.style.removeProperty("pointer-events");
              }
            }

            // For quick-add-modal: only set firstMatchingSlide once (first match)
            // For regular PDP: set firstMatchingSlide to first match (for selection)
            if (!firstMatchingSlide) {
              firstMatchingSlide = slide;
            }
          }
        }

        // After filtering, ensure controls are visible and FINAL verification of dots
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Get filtered slides (visible only)
            const filteredSlides = this.slideshow?.slides;
            if (!filteredSlides || !dots || !slidesArray) return;

            const visibleSlidesCount = filteredSlides.length;

            // FINAL VERIFICATION: Re-query dots and ensure they match filtered slides
            const mobileControlsFinal =
              this?.querySelector?.(".media-gallery__mobile-controls") || null;
            const mobileDotsContainerFinal =
              mobileControlsFinal?.querySelector?.(
                "ol.slideshow-controls__dots"
              ) || null;

            let finalDots = dots;
            if (mobileDotsContainerFinal) {
              const allDotsFinal =
                mobileDotsContainerFinal.querySelectorAll("li > button");
              finalDots = Array.from(allDotsFinal).filter(
                (d) => d instanceof HTMLElement
              );
            }

            // Ensure dots match slides - dots[i] should correspond to slidesArray[i]
            if (
              finalDots.length > 0 &&
              finalDots.length === slidesArray.length
            ) {
              finalDots.forEach((dot, i) => {
                if (!(dot instanceof HTMLElement) || i >= slidesArray.length)
                  return;

                const slide = slidesArray[i];
                const isVisible =
                  slide instanceof HTMLElement && !slide.hasAttribute("hidden");

                if (isVisible) {
                  dot.style.setProperty("display", "flex", "important");
                  dot.style.setProperty("visibility", "visible", "important");
                  dot.style.setProperty("opacity", "1", "important");
                  dot.hidden = false;
                  dot.setAttribute("data-filtered", "visible");

                  const dotLi = dot.closest("li");
                  if (dotLi instanceof HTMLElement) {
                    dotLi.style.setProperty(
                      "display",
                      "list-item",
                      "important"
                    );
                    dotLi.style.setProperty(
                      "visibility",
                      "visible",
                      "important"
                    );
                    dotLi.hidden = false;
                    dotLi.setAttribute("data-filtered", "visible");
                  }

                  // Ensure click handler is set up
                  const slideId = slide?.getAttribute?.("slide-id") || null;
                  if (dot) {
                    if (slideId) {
                      dot.setAttribute("data-slide-id", slideId);
                    } else {
                      dot.removeAttribute("data-slide-id");
                    }
                    dot.removeAttribute("on:click");
                    dot.onclick = (e) => {
                      if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                      }
                      if (
                        this.slideshow &&
                        typeof this.slideshow.select === "function"
                      ) {
                        if (slideId) {
                          this.slideshow.select({ id: slideId });
                        } else {
                          this.slideshow.select(i);
                        }
                      }
                      return false;
                    };
                    // Ensure pointer-events are enabled
                    dot.style.removeProperty("pointer-events");
                  }
                } else {
                  dot.style.setProperty("display", "none", "important");
                  dot.style.setProperty("visibility", "hidden", "important");
                  dot.style.setProperty("opacity", "0", "important");
                  dot.hidden = true;
                  dot.setAttribute("data-filtered", "hidden");

                  const dotLi = dot.closest("li");
                  if (dotLi instanceof HTMLElement) {
                    dotLi.style.setProperty("display", "none", "important");
                    dotLi.style.setProperty(
                      "visibility",
                      "hidden",
                      "important"
                    );
                    dotLi.hidden = true;
                    dotLi.setAttribute("data-filtered", "hidden");
                  }
                }
              });
            }

            // For quick-add-modal: hide all controls and dots
            if (isInQuickAddModal) {
              const slideshowControls = this.slideshow?.refs?.slideshowControls;
              if (slideshowControls instanceof HTMLElement) {
                slideshowControls.hidden = true;
                slideshowControls.style.setProperty(
                  "display",
                  "none",
                  "important"
                );
              }
              // Hide all dots container
              if (
                mobileDotsContainerFinal &&
                mobileDotsContainerFinal instanceof HTMLElement
              ) {
                mobileDotsContainerFinal.style.setProperty(
                  "display",
                  "none",
                  "important"
                );
                mobileDotsContainerFinal.hidden = true;
              }
              if (
                mobileDotsContainer &&
                mobileDotsContainer instanceof HTMLElement
              ) {
                mobileDotsContainer.style.setProperty(
                  "display",
                  "none",
                  "important"
                );
                mobileDotsContainer.hidden = true;
              }
            }

            // Ensure controls are visible if there are multiple slides (only for non-quick-add-modal)
            const slideshowControls = this.slideshow?.refs?.slideshowControls;
            if (
              !isInQuickAddModal &&
              slideshowControls instanceof HTMLElement &&
              visibleSlidesCount > 1
            ) {
              slideshowControls.hidden = false;
              slideshowControls.style.setProperty(
                "display",
                "block",
                "important"
              );

              // Ensure dots container is centered
              const dotsContainerForCentering =
                mobileDotsContainerFinal || mobileDotsContainer;
              if (dotsContainerForCentering instanceof HTMLElement) {
                dotsContainerForCentering.style.setProperty(
                  "display",
                  "flex",
                  "important"
                );
                dotsContainerForCentering.style.setProperty(
                  "justify-content",
                  "center",
                  "important"
                );
                dotsContainerForCentering.style.setProperty(
                  "align-items",
                  "center",
                  "important"
                );
                dotsContainerForCentering.hidden = false;
              }

              // Use finalDots if available, otherwise use original dots
              const dotsToUse =
                finalDots && finalDots.length > 0 ? finalDots : dots || [];
              const dotsContainer =
                dotsToUse && dotsToUse[0]
                  ? dotsToUse[0]?.closest?.("ol")
                  : null;
              if (
                dotsContainer instanceof HTMLElement &&
                dotsContainer !== dotsContainerForCentering
              ) {
                dotsContainer.style.setProperty("display", "flex", "important");
                dotsContainer.style.setProperty(
                  "justify-content",
                  "center",
                  "important"
                );
                dotsContainer.style.setProperty(
                  "align-items",
                  "center",
                  "important"
                );
                dotsContainer.hidden = false;
              }
            }
          });
        });

        // Select the first matching slide if found
        // For quick-add-modal: always select first matching slide immediately
        // For regular PDP: select with animation
        if (
          firstMatchingSlide &&
          this.slideshow &&
          typeof this.slideshow.select === "function"
        ) {
          const selectFirstSlide = () => {
            if (
              !this.slideshow ||
              typeof this.slideshow.select !== "function" ||
              !firstMatchingSlide
            ) {
              return;
            }

            // For quick-add-modal, ensure the slide is visible before selecting
            if (isInQuickAddModal) {
              // Remove hidden attribute and ensure it's visible BEFORE calling select
              // The slideshow's select method will handle reveal attribute
              firstMatchingSlide.removeAttribute("hidden");
              // Remove any inline styles that might hide it
              firstMatchingSlide.style.removeProperty("display");
              firstMatchingSlide.style.removeProperty("visibility");
              firstMatchingSlide.style.removeProperty("opacity");
            }

            // Get the slide-id to select by ID (more reliable than index)
            const slideId =
              firstMatchingSlide?.getAttribute?.("slide-id") || null;
            if (slideId) {
              // For quick-add-modal, don't animate (instant change)
              // For regular PDP, animate the transition
              const shouldAnimate = !isInQuickAddModal;
              this.slideshow.select({ id: slideId }, undefined, {
                animate: shouldAnimate,
              });

              // For quick-add-modal, ensure slide stays visible after selection
              if (isInQuickAddModal) {
                // Use setTimeout to ensure this runs after slideshow's select completes
                setTimeout(() => {
                  if (firstMatchingSlide) {
                    firstMatchingSlide.removeAttribute("hidden");
                    firstMatchingSlide.setAttribute("reveal", "");
                    firstMatchingSlide.setAttribute("aria-hidden", "false");
                    firstMatchingSlide.style.removeProperty("display");
                    firstMatchingSlide.style.removeProperty("visibility");
                    firstMatchingSlide.style.removeProperty("opacity");
                  }
                }, 0);
              }

              // Update dot selection state after selecting first slide
              setTimeout(() => {
                this.#updateDotSelectionState();
              }, 100);
              setTimeout(() => {
                this.#updateDotSelectionState();
              }, 300);
            } else {
              // Fallback to index - use all slides (refs.slides) not filtered slides
              // because filtered slides might not include hidden slides
              const allSlides = this.slideshow?.refs?.slides;
              if (allSlides) {
                const slidesArray = Array.isArray(allSlides)
                  ? allSlides
                  : Array.from(allSlides);
                const index = slidesArray.indexOf(firstMatchingSlide);
                if (index !== -1 && index >= 0) {
                  // For quick-add-modal, don't animate (instant change)
                  // For regular PDP, animate the transition
                  const shouldAnimate = !isInQuickAddModal;
                  this.slideshow.select(index, undefined, {
                    animate: shouldAnimate,
                  });

                  // For quick-add-modal, ensure slide stays visible after selection
                  if (isInQuickAddModal) {
                    // Use setTimeout to ensure this runs after slideshow's select completes
                    setTimeout(() => {
                      if (firstMatchingSlide) {
                        firstMatchingSlide.removeAttribute("hidden");
                        firstMatchingSlide.setAttribute("reveal", "");
                        firstMatchingSlide.setAttribute("aria-hidden", "false");
                        firstMatchingSlide.style.removeProperty("display");
                        firstMatchingSlide.style.removeProperty("visibility");
                        firstMatchingSlide.style.removeProperty("opacity");
                      }
                    }, 0);
                  }

                  // Update dot selection state after selecting first slide
                  setTimeout(() => {
                    this.#updateDotSelectionState();
                  }, 100);
                  setTimeout(() => {
                    this.#updateDotSelectionState();
                  }, 300);
                }
              }
            }
          };

          if (isInQuickAddModal) {
            // For quick-add-modal: immediate selection for instant image change
            selectFirstSlide();

            // Set up a continuous check to keep slide visible (more aggressive approach)
            if (
              firstMatchingSlide &&
              firstMatchingSlide instanceof HTMLElement
            ) {
              const keepVisible = () => {
                if (
                  firstMatchingSlide &&
                  firstMatchingSlide instanceof HTMLElement &&
                  firstMatchingSlide.isConnected
                ) {
                  firstMatchingSlide.removeAttribute("hidden");
                  firstMatchingSlide.setAttribute("reveal", "");
                  firstMatchingSlide.setAttribute("aria-hidden", "false");
                  if (firstMatchingSlide.style) {
                    firstMatchingSlide.style.removeProperty("display");
                    firstMatchingSlide.style.removeProperty("visibility");
                    firstMatchingSlide.style.removeProperty("opacity");
                  }
                }
              };

              // Check immediately and repeatedly
              keepVisible();
              if (typeof setTimeout !== "undefined") {
                setTimeout(keepVisible, 0);
                setTimeout(keepVisible, 50);
                setTimeout(keepVisible, 100);
                setTimeout(keepVisible, 200);
                setTimeout(keepVisible, 300);
                setTimeout(keepVisible, 500);
              }

              // Also set up interval to continuously check (will be cleaned up on disconnect)
              if (typeof setInterval !== "undefined") {
                const intervalId = setInterval(() => {
                  if (
                    firstMatchingSlide &&
                    firstMatchingSlide instanceof HTMLElement &&
                    firstMatchingSlide.isConnected
                  ) {
                    if (firstMatchingSlide.hasAttribute("hidden")) {
                      keepVisible();
                    }
                  } else {
                    if (typeof clearInterval !== "undefined") {
                      clearInterval(intervalId);
                    }
                  }
                }, 100);

                // Cleanup interval when component disconnects
                if (this.#controller && this.#controller.signal) {
                  this.#controller.signal.addEventListener("abort", () => {
                    if (typeof clearInterval !== "undefined") {
                      clearInterval(intervalId);
                    }
                  });
                }
              }
            }
          } else {
            // For regular PDP: use requestAnimationFrame for smooth transition
            requestAnimationFrame(() => {
              selectFirstSlide();
            });
          }

          // Ensure dots are visible after filtering (only for non-quick-add-modal)
          // Use multiple delays to ensure filtering has completed and DOM is updated
          if (!isInQuickAddModal) {
            // Call with multiple delays to ensure dots have data-filtered attributes set
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  this.#ensureDotsVisible();
                }, 150);
                setTimeout(() => {
                  this.#ensureDotsVisible();
                }, 300);
                setTimeout(() => {
                  this.#ensureDotsVisible();
                  // Also update dot selection state after ensuring visibility
                  setTimeout(() => {
                    this.#updateDotSelectionState();
                  }, 100);
                }, 500);
              });
            });
          }
        }
      }
      return;
    }

    // For grid layout
    if (
      this.refs.media &&
      Array.isArray(this.refs.media) &&
      this.refs.media.length > 0
    ) {
      for (let i = 0; i < this.refs.media.length; i++) {
        const mediaItem = this.refs.media[i];
        if (mediaItem instanceof HTMLElement) {
          const mediaElement =
            mediaItem?.querySelector?.("img, video, iframe") || null;
          if (mediaElement && mediaElement instanceof HTMLElement) {
            const matches = this.#mediaMatchesColor(
              mediaElement,
              normalizedColor
            );

            if (matches) {
              // Show the media item
              mediaItem.style.display = "";
            } else {
              // Hide the media item
              mediaItem.style.display = "none";
            }
          } else {
            // If no media element found, show by default
            mediaItem.style.display = "";
          }
        }
      }
    }
  }

  /**
   * Checks if a media element matches the given color based on alt text (images) or aria-label (videos).
   * @param {HTMLElement} mediaElement - The media element (img, video, or iframe).
   * @param {string} normalizedColor - The normalized color string to match.
   * @returns {boolean} - True if the media matches the color, false otherwise.
   */
  #mediaMatchesColor(mediaElement, normalizedColor) {
    // Check if this is a video element
    const isVideo =
      mediaElement.tagName === "VIDEO" ||
      mediaElement.tagName === "IFRAME" ||
      mediaElement.closest("deferred-media") !== null;

    // For videos, prioritize aria-label (since alt is not available)
    // For images, use alt attribute
    let altText = "";
    if (isVideo) {
      altText =
        mediaElement.getAttribute("aria-label") ||
        mediaElement.getAttribute("data-alt") ||
        mediaElement.getAttribute("title") ||
        "";
    } else {
      altText =
        mediaElement.getAttribute("alt") ||
        mediaElement.getAttribute("data-alt") ||
        mediaElement.getAttribute("aria-label") ||
        mediaElement.getAttribute("title") ||
        "";
    }

    const normalizedAlt = altText.toLowerCase().trim();

    // If no alt text, don't match (hide it)
    if (!normalizedAlt) {
      return false;
    }

    // Check if alt text contains the color or matches it
    const colorWords = normalizedColor
      .split(/[\s\/]+/)
      .filter((word) => word.length > 0);
    const altWords = normalizedAlt
      .split(/[\s\/]+/)
      .filter((word) => word.length > 0);

    // Check if all color words are present in alt text (exact word match)
    const allColorWordsMatch =
      colorWords.length > 0 &&
      colorWords.every(
        (colorWord) =>
          altWords.includes(colorWord) || normalizedAlt.includes(colorWord)
      );

    // Also check if alt text contains the full color string
    const containsFullColor = normalizedAlt.includes(normalizedColor);

    // Match if exact match, contains full color, or all color words match
    return (
      normalizedAlt === normalizedColor ||
      containsFullColor ||
      allColorWordsMatch
    );
  }

  /**
   * Shows all media items (removes filtering).
   * Used when the first/default variant is selected.
   */
  #showAllMedia() {
    // Check if desktop grid layout
    const isDesktopGrid =
      typeof window !== "undefined" &&
      window?.matchMedia &&
      window.matchMedia("(min-width: 750px)")?.matches &&
      this.classList?.contains("media-gallery--grid");

    // For slideshow (mobile/carousel)
    if (this.slideshow && !isDesktopGrid) {
      const slides = this.slideshow?.refs?.slides || this.slideshow?.slides;
      if (slides) {
        const slidesArray = Array.isArray(slides)
          ? slides
          : slides
          ? Array.from(slides)
          : null;
        if (slidesArray && slidesArray.length > 0) {
          const dots = this.slideshow?.refs?.dots;
          for (let i = 0; i < slidesArray.length; i++) {
            const slide = slidesArray[i];
            if (slide instanceof HTMLElement) {
              // Show all slides - remove hidden attribute and display style
              slide.removeAttribute("hidden");
              slide.style.display = "";
              // Show all dots and restore original click handlers
              if (dots && dots[i]) {
                const dot = dots[i];
                if (dot) {
                  // Remove inline display style to show the dot
                  dot.style.removeProperty("display");
                  dot.style.removeProperty("visibility");
                  dot.style.removeProperty("opacity");
                  // Set data-filtered="visible" for all dots when showing all media
                  dot.setAttribute("data-filtered", "visible");
                  dot.hidden = false;

                  const dotLi = dot.closest("li");
                  if (dotLi instanceof HTMLElement) {
                    dotLi.style.removeProperty("display");
                    dotLi.style.removeProperty("visibility");
                    // Set data-filtered="visible" for all dot li elements when showing all media
                    dotLi.setAttribute("data-filtered", "visible");
                    dotLi.hidden = false;
                  }
                  // Restore original click handler (using index)
                  const originalIndex = i;
                  dot.removeAttribute("data-slide-id");
                  dot.removeAttribute("on:click");
                  dot.onclick = (/** @type {Event} */ e) => {
                    if (e) {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                    }
                    if (
                      this.slideshow &&
                      typeof this.slideshow.select === "function" &&
                      typeof originalIndex === "number" &&
                      originalIndex >= 0
                    ) {
                      this.slideshow.select(originalIndex);
                    }
                    return false;
                  };
                }
              }
            }
          }

          // Ensure controls are visible when showing all media
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const slideshowControls = this.slideshow?.refs?.slideshowControls;
              if (slideshowControls instanceof HTMLElement) {
                slideshowControls.hidden = false;
              }

              // Update dot selection state after showing all media
              setTimeout(() => {
                this.#updateDotSelectionState();
              }, 100);
              setTimeout(() => {
                this.#updateDotSelectionState();
              }, 300);
              // Also ensure dots container is visible
              if (dots && dots.length > 0 && dots[0]) {
                const dotsContainer = dots[0]?.closest?.("ol") || null;
                if (dotsContainer instanceof HTMLElement) {
                  dotsContainer.style.removeProperty("display");
                  dotsContainer.hidden = false;
                }
              }
              // Call ensureDotsVisible to properly show all dots
              this.#ensureDotsVisible();
              // Update dot selection state after showing all media
              setTimeout(() => {
                this.#updateDotSelectionState();
              }, 100);
              setTimeout(() => {
                this.#updateDotSelectionState();
              }, 300);
            });
          });

          // Reset to first slide
          if (this.slideshow && typeof this.slideshow.select === "function") {
            requestAnimationFrame(() => {
              if (
                this.slideshow &&
                typeof this.slideshow.select === "function"
              ) {
                this.slideshow.select(0, undefined, {
                  animate: true,
                });
              }
            });
          }
        }
      }
      return;
    }

    // For grid layout
    if (
      this.refs.media &&
      Array.isArray(this.refs.media) &&
      this.refs.media.length > 0
    ) {
      for (const mediaItem of this.refs.media) {
        if (mediaItem instanceof HTMLElement) {
          mediaItem.style.display = "";
        }
      }
    }
  }

  /**
   * Initializes the media gallery with the selected variant on page load.
   * Checks URL params and variant picker state to scroll to matching media.
   */
  #initializeWithSelectedVariant() {
    // Wait for DOM and slideshow to be ready
    const waitForSlideshow = (attempts = 0) => {
      const maxAttempts = 15;

      // Check if slideshow is ready (has slides and is initialized)
      const isSlideshowReady = () => {
        if (!this.slideshow) return false;
        const slides = this.slideshow?.refs?.slides || this.slideshow?.slides;
        if (!slides) return false;
        const slidesLength = Array.isArray(slides)
          ? slides.length
          : /** @type {NodeList | HTMLCollection} */ (slides)?.length || 0;
        if (slidesLength === 0) return false;
        // Check if slideshow has scroller (means it's initialized)
        const scroller = this.slideshow?.refs?.scroller;
        return !!scroller;
      };

      if (isSlideshowReady() || attempts >= maxAttempts) {
        // Slideshow is ready or we've tried enough times
        // First, try to restore saved state (if no variant is selected)
        const stateRestored = this.#restoreSavedSlideshowState();
        // ALWAYS call initVariantMedia to filter by variant, even if state was restored
        // State restoration is just for slide position, not for variant filtering
        // Add a small delay to ensure slideshow is fully ready, especially for drawer navigation
        setTimeout(
          () => {
            this.#initVariantMedia();
          },
          stateRestored ? 300 : 200
        );
      } else if (typeof setTimeout !== "undefined") {
        // Try again after a short delay
        setTimeout(() => waitForSlideshow(attempts + 1), 100);
      }
    };

    // Start waiting for slideshow
    if (typeof document !== "undefined") {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          if (typeof setTimeout !== "undefined") {
            setTimeout(() => waitForSlideshow(0), 150);
          }
        });
      } else if (typeof setTimeout !== "undefined") {
        setTimeout(() => waitForSlideshow(0), 150);
      }
    }
  }

  /**
   * Restores saved slideshow state from localStorage (for mobile web).
   * Only restores if no variant is selected in URL or picker.
   * @returns {boolean} - Returns true if state was restored, false otherwise
   */
  #restoreSavedSlideshowState() {
    // Only restore if no variant in URL
    if (typeof window === "undefined" || !window.location) return false;
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams?.get("variant");

    // If variant is in URL, don't restore saved state (variant takes priority)
    if (variantId) return false;

    // Also check if variant is selected in picker
    const parent = this.closest(".shopify-section, dialog");
    const variantPicker = parent?.querySelector("variant-picker");
    if (variantPicker) {
      const checkedInput = variantPicker.querySelector(
        'input[type="radio"]:checked, select option:checked'
      );
      if (checkedInput) {
        const checkedVariantId =
          checkedInput instanceof HTMLElement
            ? checkedInput.dataset?.variantId
            : null;
        // If variant is selected in picker, don't restore saved state
        if (checkedVariantId) {
          const firstInput = variantPicker.querySelector(
            'input[type="radio"], select option'
          );
          const firstVariantId =
            firstInput instanceof HTMLElement
              ? firstInput.dataset?.variantId
              : null;
          // Only skip restore if it's not the first/default variant
          if (checkedVariantId !== firstVariantId) {
            return false;
          }
        }
      }
    }

    // Check if we're on mobile web with slideshow
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 749px)")?.matches;

    if (!isMobile || !this.slideshow) return false;

    try {
      const section = this.closest(".shopify-section");
      const productId =
        section instanceof HTMLElement && section.dataset?.sectionId
          ? section.dataset.sectionId
          : "default";
      const stateKey = `media-gallery-slide-${productId}`;

      if (stateKey && window.localStorage) {
        const savedIndex = localStorage.getItem(stateKey);
        if (savedIndex !== null) {
          const slideIndex = parseInt(savedIndex, 10);
          if (!isNaN(slideIndex) && slideIndex >= 0) {
            const slides =
              this.slideshow?.refs?.slides || this.slideshow?.slides;
            if (slides) {
              const slidesLength = Array.isArray(slides)
                ? slides.length
                : /** @type {NodeList | HTMLCollection} */ (slides)?.length ||
                  0;
              if (slideIndex < slidesLength) {
                // Restore saved slide index
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    if (
                      this.slideshow &&
                      typeof this.slideshow.select === "function"
                    ) {
                      this.slideshow.select(slideIndex, undefined, {
                        animate: false,
                      });
                    }
                  });
                });
                return true;
              }
            }
          }
        }
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return false;
  }

  /**
   * Initializes variant media filtering on page load.
   */
  #initVariantMedia() {
    // Check if there's a variant in URL or selected in picker
    if (typeof window === "undefined" || !window.location) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams?.get("variant");

    if (variantId) {
      // Variant in URL - filter to show only its media
      this.#filterVariantMediaFromUrl(variantId);
    } else {
      // Check variant picker for selected variant
      // Look in both .shopify-section (PDP) and dialog (quick-add-modal)
      const parent = this.closest(".shopify-section, dialog");
      if (!parent) return;
      const variantPicker = parent?.querySelector("variant-picker");
      if (variantPicker) {
        const checkedInput = variantPicker.querySelector(
          'input[type="radio"]:checked, select option:checked'
        );
        if (checkedInput) {
          const checkedVariantId =
            checkedInput instanceof HTMLElement
              ? checkedInput.dataset?.variantId
              : null;
          // Filter media for all variants (including first)
          if (checkedVariantId) {
            this.#filterVariantMedia();
          } else {
            // No variant selected, show all media
            this.#showAllMedia();
          }
        } else {
          // No variant selected, show all media
          this.#showAllMedia();
        }
      } else {
        // No variant picker found - might be loading (drawer navigation)
        // Retry after a delay
        setTimeout(() => {
          const retryParent = this.closest(".shopify-section, dialog");
          if (!retryParent) {
            this.#showAllMedia();
            return;
          }
          const retryVariantPicker =
            retryParent?.querySelector("variant-picker");
          if (retryVariantPicker) {
            const retryCheckedInput = retryVariantPicker.querySelector(
              'input[type="radio"]:checked, select option:checked'
            );
            if (retryCheckedInput) {
              const retryCheckedVariantId =
                retryCheckedInput instanceof HTMLElement
                  ? retryCheckedInput.dataset?.variantId
                  : null;
              if (retryCheckedVariantId) {
                this.#filterVariantMedia();
              } else {
                this.#showAllMedia();
              }
            } else {
              this.#showAllMedia();
            }
          } else {
            this.#showAllMedia();
          }
        }, 300);
      }
    }

    // CRITICAL: Always call ensureDotsVisible after init completes
    // Use multiple delays to ensure filtering/showAllMedia has completed
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 200);
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 400);
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 600);
      });
    });
  }

  /**
   * Filters media based on variant ID from URL.
   * @param {string} variantId - The variant ID from URL.
   */
  #filterVariantMediaFromUrl(variantId) {
    // Look in both .shopify-section (PDP) and dialog (quick-add-modal)
    const parent = this.closest(".shopify-section, dialog");
    const variantPicker = parent?.querySelector("variant-picker");

    if (!variantPicker) {
      // No variant picker found, show all media
      this.#showAllMedia();
      return;
    }

    // Find the variant option by ID
    const variantOption = variantPicker.querySelector(
      `[data-variant-id="${variantId}"]`
    );

    if (!variantOption) {
      // Variant not found, show all media
      this.#showAllMedia();
      return;
    }

    let variantColor = null;
    if (variantOption instanceof HTMLInputElement) {
      variantColor = variantOption.value;
    } else if (variantOption instanceof HTMLOptionElement) {
      variantColor = variantOption.value || variantOption.textContent;
    } else if (variantOption instanceof HTMLElement && variantOption.dataset) {
      variantColor = variantOption.dataset.optionValue || null;
    }

    if (!variantColor) {
      // No color found, show all media
      this.#showAllMedia();
      return;
    }

    const normalizedColor = String(variantColor).toLowerCase().trim();
    if (!normalizedColor) {
      this.#showAllMedia();
      return;
    }

    // Filter to show only matching media
    this.#filterMediaByColor(normalizedColor);

    // CRITICAL: Also call ensureDotsVisible after filtering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 200);
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 400);
      });
    });
  }

  /**
   * Filters media to show only the media matching the currently selected variant.
   * Gets variant color from variant picker.
   */
  #filterVariantMedia() {
    // Look in both .shopify-section (PDP) and dialog (quick-add-modal)
    const parent = this.closest(".shopify-section, dialog");
    if (!parent) {
      this.#showAllMedia();
      return;
    }
    const variantPicker = parent?.querySelector("variant-picker");

    if (!variantPicker) {
      this.#showAllMedia();
      return;
    }

    const checkedInput = variantPicker.querySelector(
      'input[type="radio"]:checked, select option:checked'
    );

    if (!checkedInput) {
      this.#showAllMedia();
      return;
    }

    let variantColor = null;
    if (checkedInput instanceof HTMLInputElement) {
      variantColor = checkedInput.value;
    } else if (checkedInput instanceof HTMLOptionElement) {
      variantColor = checkedInput.value || checkedInput.textContent;
    }

    if (!variantColor) {
      this.#showAllMedia();
      return;
    }

    const normalizedColor = String(variantColor).toLowerCase().trim();
    if (!normalizedColor) {
      this.#showAllMedia();
      return;
    }

    // Filter to show only matching media
    this.#filterMediaByColor(normalizedColor);

    // CRITICAL: Also call ensureDotsVisible after filtering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 200);
        setTimeout(() => {
          this.#ensureDotsVisible();
          setTimeout(() => {
            this.#updateDotSelectionState();
          }, 100);
        }, 400);
      });
    });
  }

  /**
   * Scrolls to the first video if it exists and has alt text.
   * Called on page load when no variant is selected.
   */
  #scrollToFirstVideoIfExists() {
    // Check if desktop grid layout
    const isDesktopGrid =
      typeof window !== "undefined" &&
      window?.matchMedia &&
      window.matchMedia("(min-width: 750px)")?.matches &&
      this.classList?.contains("media-gallery--grid");

    // For slideshow (mobile/carousel)
    if (this.slideshow && !isDesktopGrid) {
      const slides = this.slideshow?.refs?.slides || this.slideshow?.slides;
      if (slides) {
        const slidesArray = Array.isArray(slides)
          ? slides
          : slides
          ? Array.from(slides)
          : null;
        if (!slidesArray || slidesArray.length === 0) return;

        // Check first slide
        const firstSlide = slidesArray[0];
        if (firstSlide instanceof HTMLElement) {
          const mediaElement = firstSlide.querySelector("img, video, iframe");
          if (mediaElement) {
            const isVideo =
              mediaElement.tagName === "VIDEO" ||
              mediaElement.tagName === "IFRAME" ||
              mediaElement.closest("deferred-media") !== null;

            if (isVideo) {
              // Get aria-label for video
              const ariaLabel =
                mediaElement.getAttribute("aria-label") ||
                mediaElement.getAttribute("data-alt") ||
                mediaElement.getAttribute("title") ||
                "";

              // Always select first slide if it's a video (with or without aria-label)
              // Use double requestAnimationFrame to ensure slideshow is ready
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (
                    this.slideshow &&
                    typeof this.slideshow.select === "function"
                  ) {
                    this.slideshow.select(0, undefined, { animate: false });
                  }
                });
              });
              return;
            }
          }
        }
      }
    }

    // For grid layout
    if (
      isDesktopGrid &&
      this.refs.media &&
      Array.isArray(this.refs.media) &&
      this.refs.media.length > 0
    ) {
      const firstMediaItem = this.refs.media[0];
      if (firstMediaItem instanceof HTMLElement) {
        const mediaElement = firstMediaItem.querySelector("img, video, iframe");
        if (mediaElement) {
          const isVideo =
            mediaElement.tagName === "VIDEO" ||
            mediaElement.tagName === "IFRAME" ||
            mediaElement.closest("deferred-media") !== null;

          if (isVideo) {
            // Get aria-label for video
            const ariaLabel =
              mediaElement.getAttribute("aria-label") ||
              mediaElement.getAttribute("data-alt") ||
              mediaElement.getAttribute("title") ||
              "";

            // If video has alt text (aria-label), scroll to it
            if (ariaLabel.trim() !== "") {
              const isDesktop =
                typeof window !== "undefined" &&
                window?.matchMedia &&
                window.matchMedia("(min-width: 750px)")?.matches;

              const isInQuickAddModal =
                this.closest("dialog")?.classList?.contains("quick-add-modal");

              if (
                isDesktop &&
                !isInQuickAddModal &&
                typeof window !== "undefined" &&
                window.scrollTo
              ) {
                const rect = firstMediaItem.getBoundingClientRect();
                if (rect && typeof window.scrollY === "number") {
                  const scrollY = window.scrollY + rect.top - 20; // 20px offset from top
                  window.scrollTo({
                    top: scrollY,
                    behavior: "smooth",
                  });
                }
              } else if (
                firstMediaItem &&
                typeof firstMediaItem.scrollIntoView === "function"
              ) {
                firstMediaItem.scrollIntoView({
                  behavior: "smooth",
                  block: isInQuickAddModal ? "start" : "nearest",
                });
              }
            }
          }
        }
      }
    }
  }

  /**
   * Scrolls to media matching the given color.
   * @param {string} normalizedColor - The normalized color string to match.
   * @param {boolean} animate - Whether to animate the scroll.
   */
  #scrollToMediaByColor(normalizedColor, animate = true) {
    // Check if desktop grid layout
    const isDesktopGrid =
      typeof window !== "undefined" &&
      window?.matchMedia &&
      window.matchMedia("(min-width: 750px)")?.matches &&
      this.classList?.contains("media-gallery--grid");

    // For slideshow (mobile/carousel)
    if (this.slideshow && !isDesktopGrid) {
      const slides = this.slideshow?.refs?.slides || this.slideshow?.slides;
      if (slides) {
        const slidesArray = Array.isArray(slides)
          ? slides
          : slides
          ? Array.from(slides)
          : null;
        if (!slidesArray || slidesArray.length === 0) return;

        for (let i = 0; i < slidesArray.length; i++) {
          const slide = slidesArray[i];
          if (slide instanceof HTMLElement) {
            const mediaElement = slide.querySelector("img, video, iframe");
            if (mediaElement) {
              // Check if this is a video element
              const isVideo =
                mediaElement.tagName === "VIDEO" ||
                mediaElement.tagName === "IFRAME" ||
                mediaElement.closest("deferred-media") !== null;

              // For videos, prioritize aria-label (since alt is not available)
              // For images, use alt attribute
              let altText = "";
              if (isVideo) {
                altText =
                  mediaElement.getAttribute("aria-label") ||
                  mediaElement.getAttribute("data-alt") ||
                  mediaElement.getAttribute("title") ||
                  "";
              } else {
                altText =
                  mediaElement.getAttribute("alt") ||
                  mediaElement.getAttribute("data-alt") ||
                  mediaElement.getAttribute("aria-label") ||
                  mediaElement.getAttribute("title") ||
                  "";
              }
              const normalizedAlt = altText.toLowerCase().trim();

              const colorWords = normalizedColor
                .split(/[\s\/]+/)
                .filter((word) => word.length > 0);
              const altWords = normalizedAlt
                .split(/[\s\/]+/)
                .filter((word) => word.length > 0);

              const allColorWordsMatch =
                colorWords.length > 0 &&
                colorWords.every((colorWord) => {
                  const inAltWords = altWords.includes(colorWord);
                  const inAltText = normalizedAlt.includes(colorWord);
                  return inAltWords || inAltText;
                });

              const containsFullColor = normalizedAlt.includes(normalizedColor);

              // Find the FIRST matching item - return on first match
              if (
                normalizedAlt === normalizedColor ||
                containsFullColor ||
                allColorWordsMatch
              ) {
                // Use the index from the loop (i) instead of indexOf for better performance
                // This ensures we get the FIRST matching item
                const slideIndex = i;
                if (
                  slideIndex !== -1 &&
                  this.slideshow &&
                  typeof this.slideshow.select === "function"
                ) {
                  // For initial load (no animation), use double requestAnimationFrame for mobile
                  // This ensures slideshow layout is fully complete before selecting
                  if (!animate) {
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        if (
                          this.slideshow &&
                          typeof this.slideshow.select === "function"
                        ) {
                          this.slideshow.select(slideIndex, undefined, {
                            animate: false,
                          });
                        }
                      });
                    });
                  } else {
                    // For animated scrolls (variant changes), single frame is enough
                    requestAnimationFrame(() => {
                      if (
                        this.slideshow &&
                        typeof this.slideshow.select === "function"
                      ) {
                        this.slideshow.select(slideIndex, undefined, {
                          animate: true,
                        });
                      }
                    });
                  }
                  return;
                }
              }
            }
          }
        }
      }
    }

    // Fallback for grid layout - find FIRST matching item
    if (
      this.refs.media &&
      Array.isArray(this.refs.media) &&
      this.refs.media.length > 0
    ) {
      for (let i = 0; i < this.refs.media.length; i++) {
        const mediaItem = this.refs.media[i];
        if (mediaItem instanceof HTMLElement) {
          const mediaElement = mediaItem.querySelector("img, video, iframe");
          if (mediaElement) {
            // Check if this is a video element
            const isVideo =
              mediaElement.tagName === "VIDEO" ||
              mediaElement.tagName === "IFRAME" ||
              mediaElement.closest("deferred-media") !== null;

            // For videos, prioritize aria-label (since alt is not available)
            // For images, use alt attribute
            let altText = "";
            if (isVideo) {
              altText =
                mediaElement.getAttribute("aria-label") ||
                mediaElement.getAttribute("data-alt") ||
                mediaElement.getAttribute("title") ||
                "";
            } else {
              altText =
                mediaElement.getAttribute("alt") ||
                mediaElement.getAttribute("data-alt") ||
                mediaElement.getAttribute("aria-label") ||
                mediaElement.getAttribute("title") ||
                "";
            }
            const normalizedAlt = altText.toLowerCase().trim();

            const colorWords = normalizedColor
              .split(/[\s\/]+/)
              .filter((word) => word.length > 0);
            const altWords = normalizedAlt
              .split(/[\s\/]+/)
              .filter((word) => word.length > 0);

            const allColorWordsMatch =
              colorWords.length > 0 &&
              colorWords.every(
                (colorWord) =>
                  altWords.includes(colorWord) ||
                  normalizedAlt.includes(colorWord)
              );

            const containsFullColor = normalizedAlt.includes(normalizedColor);

            if (
              normalizedAlt === normalizedColor ||
              containsFullColor ||
              allColorWordsMatch
            ) {
              if (mediaItem && typeof mediaItem.scrollIntoView === "function") {
                const isDesktop =
                  typeof window !== "undefined" &&
                  window?.matchMedia &&
                  window.matchMedia("(min-width: 750px)")?.matches;

                // Check if we're in quick-add-modal (inside dialog)
                const isInQuickAddModal =
                  this.closest("dialog")?.classList?.contains(
                    "quick-add-modal"
                  );

                // For desktop PDP: scroll page so image appears at top
                // For quick-add-modal: scroll within the modal's scrollable container
                if (
                  isDesktop &&
                  !isInQuickAddModal &&
                  typeof window !== "undefined" &&
                  window.scrollTo
                ) {
                  const rect = mediaItem.getBoundingClientRect();
                  if (rect && typeof window.scrollY === "number") {
                    const scrollY = window.scrollY + rect.top - 20;
                    window.scrollTo({
                      top: scrollY,
                      behavior: animate ? "smooth" : "auto",
                    });
                  }
                } else if (
                  mediaItem &&
                  typeof mediaItem.scrollIntoView === "function"
                ) {
                  // For quick-add-modal or mobile: scroll within scrollable container
                  mediaItem.scrollIntoView({
                    behavior: animate ? "smooth" : "auto",
                    block: isInQuickAddModal ? "start" : "nearest",
                  });
                }
                return;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Resets the media gallery to show the first media.
   * Used when quick-add-modal opens to always start from the first image.
   */
  resetToFirstMedia() {
    // Check if desktop grid layout
    const isDesktopGrid =
      typeof window !== "undefined" &&
      window?.matchMedia &&
      window.matchMedia("(min-width: 750px)")?.matches &&
      this.classList?.contains("media-gallery--grid");

    // For slideshow (mobile/carousel)
    if (this.slideshow && !isDesktopGrid) {
      if (this.slideshow && typeof this.slideshow.select === "function") {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.slideshow) {
              // Check if first slide is a video with aria-label
              const slides =
                this.slideshow?.refs?.slides || this.slideshow?.slides;
              if (slides) {
                const slidesArray = Array.isArray(slides)
                  ? slides
                  : slides
                  ? Array.from(slides)
                  : null;

                if (slidesArray && slidesArray.length > 0) {
                  const firstSlide = slidesArray[0];
                  if (firstSlide instanceof HTMLElement) {
                    const mediaElement =
                      firstSlide.querySelector("img, video, iframe");
                    if (mediaElement) {
                      const isVideo =
                        mediaElement.tagName === "VIDEO" ||
                        mediaElement.tagName === "IFRAME" ||
                        mediaElement.closest("deferred-media") !== null;

                      if (isVideo) {
                        // Always select first slide if it's a video
                        this.slideshow.select(0, undefined, { animate: false });
                        return;
                      }
                    }
                  }
                }
              }

              // Default: select first slide
              this.slideshow.select(0, undefined, { animate: false });
            }
          });
        });
      }
      return;
    }

    // For grid layout: scroll to first media item
    if (
      this.refs.media &&
      Array.isArray(this.refs.media) &&
      this.refs.media.length > 0
    ) {
      const firstMediaItem = this.refs.media[0];
      if (firstMediaItem instanceof HTMLElement) {
        // Check if we're in quick-add-modal (inside dialog)
        const isInQuickAddModal =
          this.closest("dialog")?.classList?.contains("quick-add-modal");

        if (isInQuickAddModal) {
          // For quick-add-modal: scroll within the modal's scrollable container
          requestAnimationFrame(() => {
            firstMediaItem.scrollIntoView({
              behavior: "auto",
              block: "start",
            });
          });
        } else {
          // For PDP: scroll page
          const isDesktop =
            typeof window !== "undefined" &&
            window?.matchMedia &&
            window.matchMedia("(min-width: 750px)")?.matches;

          if (isDesktop && typeof window !== "undefined" && window.scrollTo) {
            requestAnimationFrame(() => {
              const rect = firstMediaItem.getBoundingClientRect();
              if (rect && typeof window.scrollY === "number") {
                const scrollY = window.scrollY + rect.top - 20;
                window.scrollTo({
                  top: scrollY,
                  behavior: "auto",
                });
              }
            });
          } else if (
            firstMediaItem &&
            typeof firstMediaItem.scrollIntoView === "function"
          ) {
            requestAnimationFrame(() => {
              firstMediaItem.scrollIntoView({
                behavior: "auto",
                block: "nearest",
              });
            });
          }
        }
      }
    }
  }

  /**
   * Sets up a MutationObserver for quick-add-modal to keep the visible slide from being hidden.
   * @param {AbortSignal} signal - Abort signal for cleanup.
   */
  #setupQuickAddModalObserver(signal) {
    if (!signal) return;

    const isInQuickAddModal =
      this.closest("dialog")?.classList?.contains("quick-add-modal") || false;
    if (!isInQuickAddModal || !this.slideshow) return;

    if (typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver((mutations) => {
      if (!this.slideshow) return;

      const visibleSlides = this.slideshow?.slides;
      if (!visibleSlides || visibleSlides.length === 0) return;

      // Get the currently selected slide
      const currentIndex =
        typeof this.slideshow.current === "number" ? this.slideshow.current : 0;
      const selectedSlide = visibleSlides[currentIndex];

      if (selectedSlide && selectedSlide instanceof HTMLElement) {
        // If the selected slide has been hidden, make it visible again
        if (selectedSlide.hasAttribute("hidden")) {
          if (typeof requestAnimationFrame !== "undefined") {
            requestAnimationFrame(() => {
              if (
                selectedSlide &&
                selectedSlide instanceof HTMLElement &&
                selectedSlide.isConnected
              ) {
                selectedSlide.removeAttribute("hidden");
                selectedSlide.setAttribute("reveal", "");
                selectedSlide.setAttribute("aria-hidden", "false");
                if (selectedSlide.style) {
                  selectedSlide.style.removeProperty("display");
                  selectedSlide.style.removeProperty("visibility");
                  selectedSlide.style.removeProperty("opacity");
                }
              }
            });
          }
        }
      }
    });

    // Observe the slideshow container for attribute changes
    const slideshowContainer = this.slideshow?.refs?.scroller;
    if (slideshowContainer && slideshowContainer instanceof HTMLElement) {
      observer.observe(slideshowContainer, {
        attributes: true,
        attributeFilter: ["hidden"],
        subtree: true,
        childList: false,
      });
    }

    // Cleanup on disconnect
    if (signal && typeof signal.addEventListener === "function") {
      signal.addEventListener("abort", () => {
        if (observer && typeof observer.disconnect === "function") {
          observer.disconnect();
        }
      });
    }
  }

  /**
   * Handles slideshow select events for quick-add-modal to ensure slide stays visible.
   * @param {Event} event - The slideshow:select event.
   */
  #handleSlideshowSelectForQuickAdd = (event) => {
    if (!event) return;
    const isInQuickAddModal =
      this.closest("dialog")?.classList?.contains("quick-add-modal") || false;
    if (!isInQuickAddModal || !this.slideshow) return;

    const customEvent = /** @type {CustomEvent<{index: number}>} */ (event);
    const selectedIndex = customEvent?.detail?.index;
    if (selectedIndex == null || typeof selectedIndex !== "number") return;

    // Get the selected slide from visible slides
    const visibleSlides = this.slideshow?.slides;
    if (!visibleSlides || !visibleSlides[selectedIndex]) return;

    const selectedSlide = visibleSlides[selectedIndex];
    if (!(selectedSlide instanceof HTMLElement)) return;

    // Ensure the selected slide stays visible in quick-add-modal
    // Use multiple requestAnimationFrame calls to ensure it happens after any DOM updates
    const ensureVisible = () => {
      if (
        selectedSlide &&
        selectedSlide instanceof HTMLElement &&
        selectedSlide.isConnected
      ) {
        selectedSlide.removeAttribute("hidden");
        selectedSlide.setAttribute("reveal", "");
        selectedSlide.setAttribute("aria-hidden", "false");
        if (selectedSlide.style) {
          selectedSlide.style.removeProperty("display");
          selectedSlide.style.removeProperty("visibility");
          selectedSlide.style.removeProperty("opacity");
        }
      }
    };

    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(() => {
        ensureVisible();
        if (typeof requestAnimationFrame !== "undefined") {
          requestAnimationFrame(() => {
            ensureVisible();
            // Also ensure it stays visible after a delay to handle async updates (like add to cart button activation)
            if (typeof setTimeout !== "undefined") {
              setTimeout(() => {
                if (
                  selectedSlide &&
                  selectedSlide instanceof HTMLElement &&
                  selectedSlide.isConnected
                ) {
                  ensureVisible();
                }
              }, 100);
              setTimeout(() => {
                if (
                  selectedSlide &&
                  selectedSlide instanceof HTMLElement &&
                  selectedSlide.isConnected
                ) {
                  ensureVisible();
                }
              }, 300);
            }
          });
        }
      });
    }
  };

  /**
   * Handles slideshow select events to save state on manual navigation.
   * @param {Event} event - The slideshow:select event.
   */
  #handleSlideshowSelect = (event) => {
    if (!event) return;
    const customEvent = /** @type {CustomEvent<{index: number}>} */ (event);
    if (customEvent?.detail?.index != null && typeof window !== "undefined") {
      // Save state for mobile web
      const isMobile =
        window.matchMedia && window.matchMedia("(max-width: 749px)")?.matches;
      if (isMobile && window.localStorage) {
        try {
          const section = this.closest(".shopify-section");
          const productId =
            section instanceof HTMLElement && section.dataset?.sectionId
              ? section.dataset.sectionId
              : "default";
          const stateKey = `media-gallery-slide-${productId}`;
          if (stateKey && customEvent.detail.index != null) {
            localStorage.setItem(stateKey, String(customEvent.detail.index));
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    }
  };

  /**
   * Handles slideshow select events to update dot aria-selected states.
   * Maps filtered slide indices to original dot indices.
   * @param {Event} event - The slideshow:select event.
   */
  #handleSlideshowSelectForDots = (event) => {
    if (!event || !this.slideshow) return;
    const customEvent = /** @type {CustomEvent<{index: number}>} */ (event);
    const filteredIndex = customEvent?.detail?.index;
    if (filteredIndex == null || filteredIndex < 0) return;

    // Get mobile dots container (for mobile web)
    const mobileControls = this.querySelector(
      ".media-gallery__mobile-controls"
    );
    const mobileDotsContainer = mobileControls?.querySelector(
      "ol.slideshow-controls__dots"
    );

    // Get dots - prioritize mobile dots, fallback to slideshow refs
    let dots = /** @type {HTMLElement[]} */ ([]);
    if (mobileDotsContainer) {
      const allDots = mobileDotsContainer.querySelectorAll("li > button");
      dots = Array.from(allDots).filter((d) => d instanceof HTMLElement);
    }

    // Fallback to slideshow refs if mobile dots not found
    if (dots.length === 0 && this.slideshow?.refs?.dots) {
      dots = Array.isArray(this.slideshow.refs.dots)
        ? this.slideshow.refs.dots.filter((d) => d instanceof HTMLElement)
        : [];
    }

    const filteredSlides = this.slideshow?.slides;
    if (
      !dots ||
      dots.length === 0 ||
      !filteredSlides ||
      filteredSlides.length === 0
    )
      return;

    // Get the selected slide from filtered slides
    const targetSlide = filteredSlides[filteredIndex];
    if (!targetSlide) return;

    // Get all slides (original order)
    const allSlides = this.slideshow?.refs?.slides;
    if (!allSlides) return;
    const slidesArray = Array.isArray(allSlides)
      ? allSlides
      : Array.from(allSlides);

    // Find the original index of the selected slide
    const originalIndex = slidesArray.indexOf(targetSlide);

    // Get visible dots (those with data-filtered="visible" or no attribute)
    const visibleDots = dots.filter((dot) => {
      if (!dot) return false;
      const filteredState = dot.getAttribute("data-filtered");
      return filteredState === "visible" || filteredState === null;
    });

    // Find which visible dot corresponds to the selected slide
    // Method 1: Use original index if found
    let selectedDot = null;
    if (originalIndex >= 0 && originalIndex < dots.length) {
      const dotAtOriginalIndex = dots[originalIndex];
      if (dotAtOriginalIndex) {
        const filteredState = dotAtOriginalIndex.getAttribute("data-filtered");
        if (filteredState === "visible" || filteredState === null) {
          selectedDot = dotAtOriginalIndex;
        }
      }
    }

    // Method 2: If not found, use filtered index to find visible dot
    // The filtered index should correspond to the visible dot index
    if (!selectedDot && filteredIndex < visibleDots.length) {
      selectedDot = visibleDots[filteredIndex];
    }

    // Update all dots: set aria-selected based on whether it's the selected dot
    dots.forEach((dot) => {
      if (dot) {
        const filteredState = dot.getAttribute("data-filtered");
        if (filteredState === "visible" || filteredState === null) {
          // Only visible dots can be selected
          const isSelected = dot === selectedDot;
          dot.setAttribute("aria-selected", isSelected ? "true" : "false");
        } else {
          // Hidden dots should not be selected
          dot.setAttribute("aria-selected", "false");
        }
      }
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (event?.detail?.index != null && this.slideshow) {
      this.slideshow.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Handles zoom clicks to correct index when media is filtered
   * @param {Event} event - The click event
   */
  #handleZoomClick = (event) => {
    if (!event) return;
    if (!this.refs?.zoomDialogComponent) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Check if this is a zoom click (slideshow-slide with zoom handler or zoom button)
    const slide = target.closest("slideshow-slide");
    const zoomButton = target.closest(
      ".product-media-container__zoom-button, button[on\\:click*='zoom']"
    );

    if (!slide && !zoomButton) return;

    // Find the clicked media element
    const clickedElement = slide || zoomButton?.closest("li");
    if (!clickedElement || !(clickedElement instanceof HTMLElement)) return;

    // Get the media ID from the clicked element
    const productMedia = clickedElement.querySelector(".product-media");
    if (!productMedia || !(productMedia instanceof HTMLElement)) return;

    const mediaId = productMedia.getAttribute("data-media-id");
    if (!mediaId) return;

    // Find the correct index in zoom dialog's media array by matching media ID
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog || !zoomDialog.refs || !zoomDialog.refs.media) return;

    const mediaArray = Array.isArray(zoomDialog.refs.media)
      ? zoomDialog.refs.media
      : Array.from(zoomDialog.refs.media);
    if (!Array.isArray(mediaArray)) return;

    const correctIndex = mediaArray.findIndex((mediaItem) => {
      if (!(mediaItem instanceof HTMLElement)) return false;
      const mediaElement = mediaItem.querySelector(".product-media");
      if (!mediaElement || !(mediaElement instanceof HTMLElement)) return false;
      const itemMediaId = mediaElement.getAttribute("data-media-id");
      return itemMediaId === mediaId;
    });

    if (correctIndex === -1 || correctIndex < 0) return;

    // Prevent the default zoom handler
    if (event.preventDefault) {
      event.preventDefault();
    }
    if (event.stopPropagation) {
      event.stopPropagation();
    }

    // Open zoom dialog with correct index
    this.zoom(correctIndex, /** @type {PointerEvent} */ (event));
  };

  /**
   * Filters zoom dialog thumbnails and media based on selected variant
   */
  #filterZoomDialogMedia() {
    if (!this.refs?.zoomDialogComponent) return;

    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog || !zoomDialog.refs) return;

    const thumbnails = zoomDialog.refs.thumbnails;
    const media = zoomDialog.refs.media;

    if (!thumbnails || !media) return;

    // Get current selected variant color
    const parent = this.closest(".shopify-section, dialog");
    if (!parent) {
      this.#showAllZoomDialogMedia();
      return;
    }

    const variantPicker = parent.querySelector("variant-picker");
    if (!variantPicker) {
      // No variant picker, show all thumbnails
      this.#showAllZoomDialogMedia();
      return;
    }

    const checkedInput = variantPicker.querySelector(
      'input[type="radio"]:checked, select option:checked'
    );
    if (!checkedInput) {
      // No variant selected, show all thumbnails
      this.#showAllZoomDialogMedia();
      return;
    }

    let variantColor = null;
    if (checkedInput instanceof HTMLInputElement) {
      variantColor = checkedInput.value;
    } else if (checkedInput instanceof HTMLOptionElement) {
      variantColor = checkedInput.value || checkedInput.textContent;
    }

    if (!variantColor) {
      this.#showAllZoomDialogMedia();
      return;
    }

    const normalizedColor = String(variantColor).toLowerCase().trim();
    if (!normalizedColor) {
      this.#showAllZoomDialogMedia();
      return;
    }

    // Filter thumbnails and media based on variant color
    if (!thumbnails.children || !Array.isArray(media)) {
      return;
    }

    const thumbnailsArray = Array.from(thumbnails.children);
    const mediaArray = Array.isArray(media) ? media : Array.from(media);

    if (!Array.isArray(thumbnailsArray) || !Array.isArray(mediaArray)) {
      return;
    }

    if (typeof this.#mediaMatchesColor !== "function") {
      return;
    }

    for (let i = 0; i < mediaArray.length; i++) {
      const mediaItem = mediaArray[i];
      const thumbnail = thumbnailsArray[i];

      if (!(mediaItem instanceof HTMLElement)) continue;

      const mediaElement =
        mediaItem.querySelector("img, video, iframe") ||
        mediaItem.querySelector(".product-media img, .product-media video");
      if (!mediaElement || !(mediaElement instanceof HTMLElement)) {
        // If no media element found, show by default
        if (mediaItem.style) {
          mediaItem.style.display = "";
        }
        if (thumbnail && thumbnail instanceof HTMLElement && thumbnail.style) {
          thumbnail.style.display = "";
        }
        continue;
      }

      const matches = this.#mediaMatchesColor(mediaElement, normalizedColor);

      if (matches) {
        // Show the media item and thumbnail
        if (mediaItem.style) {
          mediaItem.style.display = "";
        }
        if (thumbnail && thumbnail instanceof HTMLElement && thumbnail.style) {
          thumbnail.style.display = "";
        }
      } else {
        // Hide the media item and thumbnail
        if (mediaItem.style) {
          mediaItem.style.display = "none";
        }
        if (thumbnail && thumbnail instanceof HTMLElement && thumbnail.style) {
          thumbnail.style.display = "none";
        }
      }
    }
  }

  /**
   * Shows all zoom dialog thumbnails and media (removes filtering)
   */
  #showAllZoomDialogMedia() {
    if (!this.refs?.zoomDialogComponent) return;

    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog || !zoomDialog.refs) return;

    const thumbnails = zoomDialog.refs.thumbnails;
    const media = zoomDialog.refs.media;

    if (!thumbnails || !media) return;

    if (!thumbnails.children) return;

    const thumbnailsArray = Array.from(thumbnails.children);
    const mediaArray = Array.isArray(media) ? media : Array.from(media);

    if (!Array.isArray(thumbnailsArray) || !Array.isArray(mediaArray)) {
      return;
    }

    for (let i = 0; i < mediaArray.length; i++) {
      const mediaItem = mediaArray[i];
      const thumbnail = thumbnailsArray[i];

      if (mediaItem instanceof HTMLElement && mediaItem.style) {
        mediaItem.style.display = "";
      }
      if (thumbnail && thumbnail instanceof HTMLElement && thumbnail.style) {
        thumbnail.style.display = "";
      }
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (!this.refs?.zoomDialogComponent) return;
    if (index == null || isNaN(index) || index < 0) return;

    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog || typeof zoomDialog.open !== "function") return;

    zoomDialog.open(index, event);
    // Filter zoom dialog media after opening
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(() => {
        this.#filterZoomDialogMedia();
      });
    } else {
      this.#filterZoomDialogMedia();
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
